import { z } from 'zod';

/**
 * Wire primitives shared by every Conbent.Core endpoint.
 *
 * The Core service serialises with ASP.NET Core's defaults and adds only a `ByteString`
 * converter, which fixes three things the client has to handle explicitly rather than
 * assume: enums travel as numbers, protobuf timestamps travel as `{seconds, nanos}`, and
 * `DateTime` travels without a timezone designator. Each is decoded once, here, so a
 * feature deals in ISO strings and string unions.
 */

/**
 * A `google.protobuf.Timestamp` as ASP.NET Core serialises it.
 *
 * The gRPC-derived endpoints (projects, families) return this rather than a date string,
 * because their responses are protobuf messages handed straight to the JSON serialiser.
 */
export const protoTimestampSchema = z.object({
  seconds: z.union([z.number(), z.string()]),
  nanos: z.number().optional(),
});

export type ProtoTimestamp = z.infer<typeof protoTimestampSchema>;

/**
 * Converts a protobuf timestamp to an ISO string, or `null` when it carries no date.
 *
 * Unset protobuf timestamps arrive as second 0 (1970) rather than as null, and the Core
 * mappers substitute `DateTime.MinValue` (year 1) where a date is missing. Both are
 * sentinels, not dates, so both are reported as absent instead of being rendered as a
 * plausible-looking 1970.
 */
export function protoTimestampToIso(timestamp: ProtoTimestamp | null | undefined): string | null {
  if (timestamp === null || timestamp === undefined) return null;

  const seconds = Number(timestamp.seconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  const millis = seconds * 1000 + Math.round((timestamp.nanos ?? 0) / 1_000_000);
  const date = new Date(millis);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * A `DateTime` or `DateTime?` from a plain ASP.NET Core controller.
 *
 * The Core service stores UTC but serialises `DateTime` (not `DateTimeOffset`), so the
 * value arrives with no offset and `new Date` would read it as local time. Appending `Z`
 * when there is no designator keeps a timestamp from drifting by the viewer's offset.
 */
export const dotNetDateSchema = z.string().min(1);

export function dotNetDateToIso(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value.length === 0) return null;

  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return null;

  // Year 1 is `DateTime.MinValue`, which the Core service uses in place of null.
  return date.getUTCFullYear() <= 1 ? null : date.toISOString();
}

/**
 * Builds a decoder for a numeric enum.
 *
 * No `JsonStringEnumConverter` is registered on the Core service, so every enum is a bare
 * integer on the wire. Decoding to the client's string union at the boundary means a value
 * the client has no name for is caught here — where the endpoint is known — rather than
 * rendering as `undefined` in a status column.
 */
export function numericEnum<TName extends string>(
  name: string,
  members: Readonly<Record<number, TName>>
): {
  readonly schema: z.ZodType<TName, z.ZodTypeDef, number>;
  readonly decode: (value: number) => TName | null;
} {
  const decode = (value: number): TName | null => members[value] ?? null;

  const schema = z.number().int().transform((value, ctx) => {
    const decoded = decode(value);
    if (decoded === null) {
      ctx.addIssue({
        code: 'custom',
        message: `${String(value)} is not a known ${name}.`,
      });
      return z.NEVER;
    }
    return decoded;
  });

  return { schema, decode };
}

/**
 * A `Guid`, which the Core service writes in the hyphenated 8-4-4-4-12 form.
 *
 * Not validated beyond being a non-empty string: the client only ever echoes these back or
 * uses them as opaque keys, and rejecting a response over a malformed hash would be a
 * worse outcome than carrying it.
 */
export const guidSchema = z.string();

/**
 * Parses a size the Core service reports as text.
 *
 * `SnapshotMetaData.SnapshotFileSize` is a `string` holding a byte count, occasionally with
 * a unit suffix. Anything unparseable reads as absent rather than zero, so the UI can say
 * "unknown" instead of claiming an empty file.
 */
export function parseByteSize(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;

  const match = /^\s*([\d.,]+)\s*([kmgt]?b)?\s*$/i.exec(value);
  if (match === null) return null;

  const magnitude = Number(match[1]?.replace(/,/g, ''));
  if (!Number.isFinite(magnitude)) return null;

  const multipliers: Readonly<Record<string, number>> = {
    b: 1,
    kb: 1024,
    mb: 1024 ** 2,
    gb: 1024 ** 3,
    tb: 1024 ** 4,
  };

  return magnitude * (multipliers[match[2]?.toLowerCase() ?? 'b'] ?? 1);
}

/**
 * A `/api/v1` timestamp: ISO-8601 UTC with the `Z` designator. Unlike the legacy routes'
 * `DateTime`, a value without a designator is a contract break and fails to parse.
 */
export const v1DateSchema = z.string().datetime();

/**
 * A required `/api/v1` string enum whose unrecognised values decode to `Unknown`.
 *
 * Core adds enum members additively, so a newer server must not blank a whole list; a
 * missing value is still an error.
 */
export function tolerantEnum<const TValues extends readonly string[]>(values: TValues) {
  return z
    .string()
    .transform((value): TValues[number] | 'Unknown' =>
      values.includes(value) ? (value as TValues[number]) : 'Unknown'
    );
}
